import React from 'react';
import {
  AttachmentPreviewUtil,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Layout,
} from '@axiom/ui';

import type {
  ChatAttachment,
  ChatMessageAttachment,
  ChatMessageEntry,
} from '../../types/chat.types';

import { ChatAttachmentListItem } from './ChatAttachmentListItem';

export type ChatAttachmentListProps = { name: string } & (
  | {
      /** The composer's shelf: every file a removable row. */
      attachments: ChatAttachment[];
      chatId?: never;
      images?: never;
      removeAttachment: (attachmentId: ChatAttachment['id']) => void;
    }
  | {
      /** A sent turn's files, in the order they were attached. */
      attachments: ChatMessageAttachment[];
      chatId: ChatMessageEntry['chatId'];
      /** `thumbnail` lifts the pictures out into a row of tiles above the other files. */
      images?: 'thumbnail' | 'row';
      removeAttachment?: never;
    }
);

/**
 * ChatAttachmentList - I lay out a set of attached files. ONLY concerned with which file goes where
 * - each file draws itself.
 */
export const ChatAttachmentList = ({
  attachments,
  chatId,
  images = 'row',
  name,
  removeAttachment,
}: ChatAttachmentListProps) => {
  const sent = attachments.flatMap(attachment =>
    'file' in attachment ? [] : [attachment]
  );

  const thumbnails =
    images === 'thumbnail'
      ? sent.filter(({ fileName }) =>
          AttachmentPreviewUtil.isDisplayableImage({ fileName })
        )
      : [];

  const rows = attachments.filter(
    attachment => 'file' in attachment || !thumbnails.includes(attachment)
  );

  return (
    <Layout
      direction="vertical"
      name={name}
      verticalGutter={removeAttachment ? '12px' : '8px'}
    >
      {thumbnails.length > 0 ? (
        // Reversed so a short row of pictures sits against the right edge, under the prompt.
        <Grid
          name={`${name}_THUMBNAILS`}
          reversed="mobile tablet computer widescreen"
        >
          <GridRow>
            {thumbnails.map(({ fileId, fileName }, index) => (
              <GridColumn key={fileId ?? `${fileName}-${index}`} width={4}>
                <Gutter bottom="8px">
                  <ChatAttachmentListItem
                    attachment={{ fileId, fileName }}
                    chatId={chatId}
                    display="thumbnail"
                    name={`${name}_THUMBNAIL_${index}`}
                  />
                </Gutter>
              </GridColumn>
            ))}
          </GridRow>
        </Grid>
      ) : null}
      {rows.map((attachment, index) =>
        'file' in attachment ? (
          <ChatAttachmentListItem
            attachment={attachment}
            key={attachment.id}
            name={`${name}_FILE_${index}`}
            onRemove={() => {
              removeAttachment?.(attachment.id);
            }}
          />
        ) : (
          <ChatAttachmentListItem
            attachment={attachment}
            chatId={chatId}
            key={attachment.fileId ?? `${attachment.fileName}-${index}`}
            name={`${name}_FILE_${index}`}
          />
        )
      )}
    </Layout>
  );
};
